import { Dispatch, useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';

import { INKOForeignAgent } from 'APIPackage/CatalogsModule/interfaces/INKOForeignAgent';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_SHORT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import {
  knmInfoGetNKOForeignAgentDataByOgrn,
  knmInfoSetNKOForeignAgentDataByOgrn,
} from 'KnmPackage/MainModule/actions/knmActions';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef, getNKOForeignAgentData } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Row } from 'UIPackage/FormModule/components/Row';

import { KnmNkoField } from '../../MainModule/components/KnmNkoField';

interface IKnmSubjectIsNkoProps {
  checkByInnMessage?: string;
  name: string | undefined;
  ogrn: string | undefined;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
}

interface IKnmSubjectIsNkoStoreProps {
  formRef: IKnmFormRef | undefined;
  nkoForeignAgentData: INKOForeignAgent | undefined;
}

const KnmSubjectIsNkoWithoutStore = ({
  checkByInnMessage,
  name: parentName,
  ogrn,
  formRef,
  nkoForeignAgentData,
  subjectType,
}: IKnmSubjectIsNkoProps & IKnmSubjectIsNkoStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [nkoContentLabel, setNkoContentLabel] = useState('');
  const name = parentName ? `${parentName}.` : '';

  const nkoForeignAgentsRegistryNumberFieldName = `${name}nkoForeignAgentsRegistryNumber` as keyof IKnmForm;
  const nkoForeignAgentsRegistryInclusionDateFieldName = `${name}nkoForeignAgentsRegistryInclusionDate` as keyof IKnmForm;
  const nkoForeignAgentsRegistryExclusionDateFieldName = `${name}nkoForeignAgentsRegistryExclusionDate` as keyof IKnmForm;
  const isNkoFieldName = `${name}isNko` as keyof IKnmForm;

  useEffect(() => {
    if (subjectType?.label === KnmSubjectTypesEnum.UR) {
      if (ogrn) {
        if (ogrn[0] !== '0' && (/^[\d+]{13}$/.test(ogrn) || /^[\d+]{15}$/.test(ogrn))) {
          dispatch(knmInfoGetNKOForeignAgentDataByOgrn(ogrn));
          setNkoContentLabel('Нет');
          formRef?.current.change(isNkoFieldName);
        } else {
          setNkoContentLabel('');
          dispatch(knmInfoSetNKOForeignAgentDataByOgrn(undefined));
          formRef?.current.change(isNkoFieldName);
        }
      } else {
        setNkoContentLabel('');
        dispatch(knmInfoSetNKOForeignAgentDataByOgrn(undefined));
        formRef?.current.change(isNkoFieldName);
      }
    }
  }, [dispatch, ogrn, subjectType, isNkoFieldName, formRef]);

  useEffect(() => {
    if (nkoForeignAgentData && ogrn) {
      setNkoContentLabel('Да');
      formRef?.current.change(nkoForeignAgentsRegistryNumberFieldName, nkoForeignAgentData.registryNumber);
      formRef?.current.change(isNkoFieldName, true);
      formRef?.current.change(
        nkoForeignAgentsRegistryInclusionDateFieldName,
        formatDateLocale({
          date: nkoForeignAgentData.inclusionDate,
          dateFormat: DATE_FORMAT_LONG_FNS,
          parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
        }),
      );
      if (nkoForeignAgentData.exclusionDate) {
        formRef?.current.change(
          nkoForeignAgentsRegistryExclusionDateFieldName,
          formatDateLocale({
            date: nkoForeignAgentData.exclusionDate,
            dateFormat: DATE_FORMAT_LONG_FNS,
            parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
          }),
        );
      } else {
        formRef?.current.change(nkoForeignAgentsRegistryExclusionDateFieldName, undefined);
      }
    } else if (ogrn && (ogrn[0] === '0' || !(/^[\d+]{13}$/.test(ogrn) || /^[\d+]{15}$/.test(ogrn)))) {
      setNkoContentLabel('');
      formRef?.current.change(isNkoFieldName);
    }
  }, [nkoForeignAgentData, formRef, name, ogrn]);

  return (
    <>
      {/* Блок информации о наличии контролируемого лица в реестре НКО-иноагентов */}
      {subjectType?.label === KnmSubjectTypesEnum.UR && (
        <Row>
          <KnmNkoField
            id={`${name}isNko`}
            value={nkoContentLabel}
            name={`${name}isNko`}
            formRef={formRef}
            warningMessage={checkByInnMessage}
          />
        </Row>
      )}
      <Field name={nkoForeignAgentsRegistryNumberFieldName}>{() => null}</Field>
      <Field name={nkoForeignAgentsRegistryInclusionDateFieldName}>{() => null}</Field>
      <Field name={nkoForeignAgentsRegistryExclusionDateFieldName}>{() => null}</Field>
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmSubjectIsNkoStoreProps => ({
  formRef: getKnmFormRef(state),
  nkoForeignAgentData: getNKOForeignAgentData(state),
});

export const KnmSubjectIsNko = connect(mapStateToProps)(KnmSubjectIsNkoWithoutStore);
